import React, { useState, useEffect, useMemo, useRef } from 'react';
import styles from './BankAccounts.module.css';
import bankData from '../../data/banks.json';

const EMPTY = {
  accountOwnerName: '',
  bankName: '',
  bankNumber: '',
  branchName: '',
  branchNumber: '',
  accountNumber: '',
};

export default function BankAccountForm({ account, onSave, onCancel }) {
  const [form, setForm] = useState(EMPTY);
  const [bankSearch, setBankSearch] = useState('');
  const [showBanks, setShowBanks] = useState(false);
  const [branchSearch, setBranchSearch] = useState('');
  const [showBranches, setShowBranches] = useState(false);
  const bankRef = useRef(null);
  const branchRef = useRef(null);

  useEffect(() => {
    if (account) {
      setForm(account);
      setBankSearch(account.bankName || '');
      setBranchSearch(account.branchName || '');
    } else {
      setForm(EMPTY);
      setBankSearch('');
      setBranchSearch('');
    }
  }, [account]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (bankRef.current && !bankRef.current.contains(e.target)) setShowBanks(false);
      if (branchRef.current && !branchRef.current.contains(e.target)) setShowBranches(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const banks = bankData.banks;

  const filteredBanks = useMemo(() => {
    if (!bankSearch) return banks;
    const q = bankSearch.toLowerCase();
    return banks.filter(b => b.name.includes(q) || String(b.code).includes(q));
  }, [bankSearch, banks]);

  const filteredBranches = useMemo(() => {
    if (!form.bankNumber) return [];
    const bankCode = parseInt(form.bankNumber);
    const bankBranches = bankData.branches.filter(b => b.bankCode === bankCode);
    if (!branchSearch) return bankBranches.slice(0, 30);
    const q = branchSearch.toLowerCase();
    return bankBranches
      .filter(b => b.name.includes(q) || b.city.includes(q) || String(b.code).includes(q))
      .slice(0, 30);
  }, [form.bankNumber, branchSearch]);

  const handleBankSelect = (bank) => {
    setForm(f => ({ ...f, bankNumber: String(bank.code), bankName: bank.name, branchNumber: '', branchName: '' }));
    setBankSearch(bank.name);
    setBranchSearch('');
    setShowBanks(false);
  };

  const handleBranchSelect = (branch) => {
    setForm(f => ({ ...f, branchNumber: String(branch.code), branchName: `${branch.name} - ${branch.city}` }));
    setBranchSearch(`${branch.name} - ${branch.city}`);
    setShowBranches(false);
  };

  const handleBankSearchChange = (e) => {
    setBankSearch(e.target.value);
    setShowBanks(true);
    // Clear bank selection if user is typing something new
    if (form.bankNumber) {
      setForm(f => ({ ...f, bankNumber: '', bankName: '', branchNumber: '', branchName: '' }));
      setBranchSearch('');
    }
  };

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = () => {
    if (!form.accountOwnerName || !form.bankNumber || !form.accountNumber) {
      alert('נא למלא את כל שדות החובה');
      return;
    }
    if (!form.branchNumber) {
      alert('נא לבחור סניף מהרשימה');
      return;
    }
    onSave(form);
  };

  return (
    <div className={styles.form}>
      <h3>{account ? 'עריכת חשבון' : 'חשבון בנק חדש'}</h3>

      <label>שם בעל החשבון *
        <input name="accountOwnerName" value={form.accountOwnerName} onChange={handle} required />
      </label>

      <label>בנק *
        <div className={styles.autocomplete} ref={bankRef}>
          <input
            value={bankSearch}
            onChange={handleBankSearchChange}
            onFocus={() => setShowBanks(true)}
            placeholder="הקלד שם בנק או מספר..."
          />
          {showBanks && filteredBanks.length > 0 && (
            <ul className={styles.suggestions}>
              {filteredBanks.map((b) => (
                <li key={b.code} onClick={() => handleBankSelect(b)}>
                  <strong>{b.code}</strong> — {b.name}
                </li>
              ))}
            </ul>
          )}
        </div>
      </label>

      <label>סניף *
        <div className={styles.autocomplete} ref={branchRef}>
          <input
            value={branchSearch}
            onChange={(e) => { setBranchSearch(e.target.value); setShowBranches(true); }}
            onFocus={() => setShowBranches(true)}
            placeholder={form.bankNumber ? 'הקלד שם סניף, עיר או מספר...' : 'בחר בנק תחילה'}
            disabled={!form.bankNumber}
          />
          {showBranches && filteredBranches.length > 0 && (
            <ul className={styles.suggestions}>
              {filteredBranches.map((b) => (
                <li key={`${b.bankCode}-${b.code}`} onClick={() => handleBranchSelect(b)}>
                  <strong>{b.code}</strong> — {b.name}, {b.city}
                </li>
              ))}
            </ul>
          )}
        </div>
      </label>

      <label>מספר חשבון *
        <input name="accountNumber" value={form.accountNumber} onChange={handle} required />
      </label>

      <div className={styles.formActions}>
        <button type="button" className={styles.btnPrimary} onClick={submit}>שמור</button>
        <button type="button" className={styles.btnSecondary} onClick={onCancel}>ביטול</button>
      </div>
    </div>
  );
}
